import { useDispatch } from 'react-redux'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore.ts'
import { VALID_TRIP_TYPE_SWAPS } from '../utils/constants'
import { DISPATCH_TICKET_TYPES } from 'utils/constants'
import { resetAssets, setAssets } from 'state/dispatch/index'

export const useHandleTypeChange = ({ ticketHasAssets, setAttemptedNewType, setShowTypeChangeModal, methods }) => {
  const { watch, setValue } = methods
  const dispatch = useDispatch()
  const { pickupAssets, dropoffAssets, pickAndReturnAssets, liveLoadAssets } = useAppSelector(state => state.dispatch)

  const selectedDispatchTicketType = watch('type')
  const pickUpTrailer = watch('pickUpTrailer')
  const pickAndReturnTrailer = watch('pickAndReturnTrailer')

  /**
   * This handles the removal of all assets when an invalid type change is made
   */
  const handleTaskTypeChangeConfirm = attemptedNewType => {
    setShowTypeChangeModal(false)
    dispatch(resetAssets())

    setValue('pickupAssets', [], { shouldDirty: true })
    setValue('dropoffAssets', [], { shouldDirty: true })
    setValue('pickAndReturnAssets', [], { shouldDirty: true })
    setValue('deliveryAssets', [], { shouldDirty: true })
    setValue('liveLoadAssets', [], { shouldDirty: true })

    setValue('dropOffTrailer', false, { shouldDirty: true })
    setValue('pickUpTrailer', false, { shouldDirty: true })
    setValue('pickAndReturnTrailer', false, { shouldDirty: true })

    setValue('type', attemptedNewType, { shouldDirty: true })
  }

  /**
   * This replaces the default form dropdown handler. It primarily translates
   * the assets attached to the ticket to match the new type selected
   */
  const onTypeChange = newType => {
    // Logic: No type change --> No assets need updating --> Assets need to be updated --> Assets need to be removed
    if (newType === selectedDispatchTicketType) {
      return
    } else if (!ticketHasAssets) {
      setValue('type', newType, { shouldDirty: true })
      return
    } else if (VALID_TRIP_TYPE_SWAPS[selectedDispatchTicketType].includes(newType)) {
      // From Pickup:
      if (selectedDispatchTicketType === DISPATCH_TICKET_TYPES.PICK_UP.id) {
        if (newType === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) {
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id, assets: pickupAssets }))
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_UP.id, assets: [] }))

          setValue('pickUpTrailer', false, { shouldDirty: true })
          setValue('pickAndReturnTrailer', pickUpTrailer, { shouldDirty: true })
        }
      }

      // From Dropoff:
      if (selectedDispatchTicketType === DISPATCH_TICKET_TYPES.DROP_OFF.id) {
        if (newType === DISPATCH_TICKET_TYPES.LIVE_LOAD.id) {
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.LIVE_LOAD.id, assets: dropoffAssets }))
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.DROP_OFF.id, assets: [] }))
        }

        setValue('dropOffTrailer', false, { shouldDirty: true })
        setValue('pickUpTrailer', false, { shouldDirty: true })
        setValue('pickAndReturnTrailer', false, { shouldDirty: true })
      }

      // From Exchange:
      if (selectedDispatchTicketType === DISPATCH_TICKET_TYPES.EXCHANGE.id) {
        if (newType === DISPATCH_TICKET_TYPES.DROP_OFF.id) {
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_UP.id, assets: [] }))
        } else if (newType === DISPATCH_TICKET_TYPES.PICK_UP.id) {
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.DROP_OFF.id, assets: [] }))
        } else if (newType === DISPATCH_TICKET_TYPES.LIVE_LOAD.id) {
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.LIVE_LOAD.id, assets: dropoffAssets }))
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_UP.id, assets: [] }))
        }

        setValue('dropOffTrailer', false, { shouldDirty: true })
        setValue('pickUpTrailer', false, { shouldDirty: true })
        setValue('pickAndReturnTrailer', false, { shouldDirty: true })
      }

      // From Live Load:
      if (selectedDispatchTicketType === DISPATCH_TICKET_TYPES.LIVE_LOAD.id) {
        dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.DROP_OFF.id, assets: liveLoadAssets }))
        dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.LIVE_LOAD.id, assets: [] }))

        setValue('dropOffTrailer', false, { shouldDirty: true })
        setValue('pickUpTrailer', false, { shouldDirty: true })
        setValue('pickAndReturnTrailer', false, { shouldDirty: true })
      }

      // From Pick & Return:
      if (selectedDispatchTicketType === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) {
        if (newType === DISPATCH_TICKET_TYPES.PICK_UP.id) {
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_UP.id, assets: pickAndReturnAssets }))
          dispatch(setAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id, assets: [] }))

          setValue('dropOffTrailer', false, { shouldDirty: true })
          setValue('pickUpTrailer', pickAndReturnTrailer, { shouldDirty: true })
          setValue('pickAndReturnTrailer', false, { shouldDirty: true })
        }
      }

      setValue('pickupAssets', [], { shouldDirty: true })
      setValue('dropoffAssets', [], { shouldDirty: true })
      setValue('pickAndReturnAssets', [], { shouldDirty: true })
      setValue('deliveryAssets', [], { shouldDirty: true })
      setValue('liveLoadAssets', [], { shouldDirty: true })

      setValue('type', newType, { shouldDirty: true })
    } else {
      setAttemptedNewType(newType)
      setShowTypeChangeModal(true)
    }
  }

  return {
    onTypeChange,
    handleTaskTypeChangeConfirm,
  }
}
